import { useTranslate } from 'react-admin';
import { Button, Stack, Typography } from '@mui/material';

import type { QueueList } from 'src/pages/admin/services/vaultOperationsService';
import type { ApprovalRow } from 'src/pages/admin/services/vaultService';

import { OPERATIONS_NS as NS, OUTCOME_BUTTON_SX } from './constants';
import { durationText, minutesUntil, money2 } from './helpers';
import { AddressLink, EmptyState, ItemCard, ListNotices, TabIntro, TimeText, useNow } from './parts';
import {
  isKnownStatus,
  laneKey,
  readableReason,
  statusKey,
  turnkeyUrl,
  waitingSinceOf,
} from './signers';
import type { Queue } from './useQueue';

interface Props {
  queue: Queue<QueueList<ApprovalRow>>;
  vaultName: (vaultId: string | null | undefined) => string;
}

/**
 * "Waiting for signers" (XBIT-4950, the former Approvals page): treasury orders that wait on a person. Read-only: nothing is
 * signed in the console; the button opens the order's activity in Turnkey, where the signers approve it.
 */
export const SignersTab = ({ queue, vaultName }: Props) => {
  const translate = useTranslate();
  const now = useNow();
  const t = (key: string, options?: object) => translate(`${NS}.signers.${key}`, options);
  const items = queue.data?.items ?? [];
  return (
    <Stack spacing={2}>
      <TabIntro tab="signers" />
      {queue.data && (
        <ListNotices
          consistent={queue.data.consistent}
          truncated={queue.data.truncated}
          shown={items.length}
          onRefresh={queue.refetch}
        />
      )}
      {queue.data && items.length === 0 && (
        <EmptyState tab="signers" checkedAt={queue.data.checkedAt} />
      )}
      {items.map((row) => {
        const lane = laneKey(row.lane);
        const status = statusKey(row.status);
        // the vault by name; one the catalog does not know reads as unknown here, its id is under Technical details
        const named = row.vaultId ? vaultName(row.vaultId) : null;
        const vault = named && named !== row.vaultId ? named : null;
        const reason = readableReason(row.stuckReason);
        const href = turnkeyUrl(row.activityId);
        const waited = minutesUntil(waitingSinceOf(row), now);
        const sentence = {
          lane: t(`lane.${lane}`),
          amount: money2(row.amount),
        };
        return (
          <ItemCard
            key={row.workOrderId}
            testId={`signer-${row.workOrderId}`}
            title={vault ? `${vault} — ${t(`lane.${lane}`)}` : t(`lane.${lane}`)}
            status="NEEDS_ACTION"
            whatHappened={t(`what.${status}`, sentence)}
            affected={t(`affected.${lane}`, { vault: vault ?? t('unknownVault') })}
            extra={
              <Stack spacing={0.5}>
                <Typography variant="body2" component="div">
                  {t('destination')} <AddressLink address={row.destination} />
                </Typography>
                {/*
                  XBIT-3988: a held order is still listed here, because gate A holds the MINT — so it looks like it is
                  waiting for a signer when signing will not move it. Said BEFORE stuckReason, which carries the reason
                  of the last transition and is therefore stale on an order nothing has transitioned.
                */}
                {row.heldBy && (
                  <Typography variant="body2" sx={{ color: 'warning.main' }}>
                    {t('heldBy', { sources: row.heldBy })}
                  </Typography>
                )}
                {row.stuckReason && (
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    {reason ? t('reasonIs', { reason }) : t('reasonRecorded')}
                  </Typography>
                )}
              </Stack>
            }
            deadline={
              <>
                {t('noDeadline')}{' '}
                {waited === null
                  ? null
                  : t('waitingFor', { duration: durationText(waited, translate) })}{' '}
                <TimeText iso={waitingSinceOf(row)} />
              </>
            }
            actions={
              row.status === 'AWAITING_QUORUM' ? (
                href && row.activityLive ? (
                  <Button
                    sx={OUTCOME_BUTTON_SX}
                    variant="contained"
                    component="a"
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {t('openTurnkey')}
                  </Button>
                ) : (
                  <Stack spacing={0.5} sx={{ alignItems: 'flex-start' }}>
                    <Button sx={OUTCOME_BUTTON_SX} variant="contained" disabled>
                      {t('openTurnkey')}
                    </Button>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                      {t(href ? 'activityClosed' : 'noActivity')}
                    </Typography>
                  </Stack>
                )
              ) : (
                <Typography variant="body2">
                  {t(isKnownStatus(row.status) ? 'nothingToDo' : 'askEngineer')}
                </Typography>
              )
            }
            technical={{
              workOrderId: row.workOrderId,
              lane: row.lane,
              status: row.status,
              heldBy: row.heldBy ?? null,
              stuckReason: row.stuckReason,
              vaultId: row.vaultId,
              redemptionRecordId: row.redemptionRecordId,
              destination: row.destination,
              activityId: row.activityId,
              activityLive: row.activityLive,
              waitingSince: row.waitingSince,
              createdAt: row.createdAt,
            }}
          />
        );
      })}
    </Stack>
  );
};
